import { h, r as registerInstance, c as createEvent, f as forceUpdate, H as Host, g as getElement } from './index-5aea3fea.js';
import { i as i18nProvider, l as loadTranslations } from './loadTranslations-f63b1e5d.js';
import { s as setEnv, g as getEnvConfig, a as getPublicApiClient, c as authClientName } from './publicApiClient-8f578e40.js';

const ANOMALY = {
    DETECTED: "detected",
    NOT_DETECTED: "not_detected"
};
const TECHNICAL_GUIDE_STATE = {
    NONE: "none",
    HELPFUL: "helpful",
    NOT_HELPFUL: "not_helpful"
};
const PAUSE_REQUEST = {
    NONE: "none",
    DAYS_7: "7_days",
    DAYS_14: "14_days",
    DAYS_30: "30_days",
    DAYS_90: "90_days"
};
const FIRST_QUESTION_KEY = "anomaly";
const TECHNICAL_GUIDE_LINKED = "technicalGuideLinked";
const FOLLOWUP_QUESTION = "followupQuestion"; // this is used for a value that is only used to trigger a follow-up question

const buildRequestData = ({ anomaly, technicalGuideHelpful, technicalGuideLinked, pauseRequest, pauseRequestDuration, comment }, callId) => {
    return {
        callId,
        anomaly,
        technicalGuideState: (technicalGuideLinked !== FOLLOWUP_QUESTION ? technicalGuideLinked : technicalGuideHelpful) ||
            TECHNICAL_GUIDE_STATE.NONE,
        pauseRequest: (pauseRequest !== FOLLOWUP_QUESTION ? pauseRequest : pauseRequestDuration) || PAUSE_REQUEST.NONE,
        comment: comment || null
    };
};

const questions = {
    anomaly: {
        name: "anomaly",
        labelKey: "serviceCase.proactiveCaseClosure.question.anomaly",
        options: {
            [ANOMALY.DETECTED]: { labelKey: "serviceCase.proactiveCaseClosure.answers.yes", next: "technicalGuideLinked" },
            [ANOMALY.NOT_DETECTED]: { labelKey: "serviceCase.proactiveCaseClosure.answers.no", next: "pauseRequest" }
        }
    },
    technicalGuideLinked: {
        invisible: true,
        name: "technicalGuideLinked",
        labelKey: "serviceCase.proactiveCaseClosure.question.technicalGuideLinked",
        options: {
            [FOLLOWUP_QUESTION]: { labelKey: "serviceCase.proactiveCaseClosure.answers.yes", next: "technicalGuideHelpful" },
            [TECHNICAL_GUIDE_STATE.NONE]: { labelKey: "serviceCase.proactiveCaseClosure.answers.no", next: "pauseRequest" }
        }
    },
    technicalGuideHelpful: {
        name: "technicalGuideHelpful",
        labelKey: "serviceCase.proactiveCaseClosure.question.technicalGuideHelpful",
        options: {
            [TECHNICAL_GUIDE_STATE.HELPFUL]: { labelKey: "serviceCase.proactiveCaseClosure.answers.yes", next: "pauseRequest" },
            [TECHNICAL_GUIDE_STATE.NOT_HELPFUL]: { labelKey: "serviceCase.proactiveCaseClosure.answers.no", next: "pauseRequest" }
        }
    },
    pauseRequest: {
        name: "pauseRequest",
        labelKey: "serviceCase.proactiveCaseClosure.question.pauseRequest",
        options: {
            [FOLLOWUP_QUESTION]: { labelKey: "serviceCase.proactiveCaseClosure.answers.yes", next: "pauseRequestDuration" },
            [PAUSE_REQUEST.NONE]: { labelKey: "serviceCase.proactiveCaseClosure.answers.no" }
        }
    },
    pauseRequestDuration: {
        name: "pauseRequestDuration",
        labelKey: "serviceCase.proactiveCaseClosure.question.pauseRequestDuration",
        options: {
            [PAUSE_REQUEST.DAYS_7]: { labelKey: "serviceCase.proactiveCaseClosure.answers.sevenDays" },
            [PAUSE_REQUEST.DAYS_14]: { labelKey: "serviceCase.proactiveCaseClosure.answers.fourteenDays" },
            [PAUSE_REQUEST.DAYS_30]: { labelKey: "serviceCase.proactiveCaseClosure.answers.thirtyDays" },
            [PAUSE_REQUEST.DAYS_90]: { labelKey: "serviceCase.proactiveCaseClosure.answers.ninetyDays" }
        },
        comment: {
            name: "comment",
            labelKey: "serviceCase.proactiveCaseClosure.question.comment",
            placeholderKey: "serviceCase.proactiveCaseClosure.question.commentPlaceholder"
        }
    }
};

const sendCaseClosureRequest = async (apiClient, data) => {
    return apiClient.request("/servicecall/proactivecaseclosure", { body: data });
};

/**
 * functional stencil component for a question with radio buttons
 */
function QuestionRadio({ question, answers, setAnswers, t, baseClass }) {
    return (h("fieldset", null,
        h("legend", { class: "mf-proactive-case-closure__question" }, t(question.labelKey)),
        h("div", { class: `${baseClass}__radio-buttons-container` },
            h("div", { class: `${baseClass}__radio-buttons` }, Object.entries(question.options).map(([value, option]) => (h("ux-radio-button", { key: value, type: "radio", name: question.name, value: value, checked: answers[question.name] === value, onUxRadioButtonChange: (e) => {
                    setAnswers(Object.assign(Object.assign({}, answers), { [question.name]: e.detail.value }));
                } }, t(option.labelKey))))))));
}

/**
 * functional stencil component for a question with a comment field
 */
function QuestionComment({ label, name, answers, setAnswers, placeholder }) {
    return (h("div", null,
        h("div", { class: "mf-proactive-case-closure__question ux-u-margin-top-40 ux-u-margin-bottom-20" }, label),
        h("div", null,
            h("ux-textarea-field", { name: name, placeholder: placeholder, onUxTextareaFieldChange: (e) => setAnswers(Object.assign(Object.assign({}, answers), { [name]: e.detail.value })) }))));
}

const getTechnicalGuides = async (apiClient, callId) => {
    if (!apiClient) {
        return Promise.reject("PublicApiClient is not initialized");
    }
    return apiClient === null || apiClient === void 0 ? void 0 : apiClient.request("/technicalguide/getallforcase", {
        body: { callId },
        mockResponse: mockTechnicalGuides
    });
};
const mockTechnicalGuides = [
    {
        technicalGuideId: "470090",
        descriptions: [
            {
                description: "+++ This text is machine translated +++\nTG 58102409 Kolizn\u00ed laserov\u00e1 \u0159ezac\u00ed hlava",
                language: "cs"
            },
            {
                description: "TG 58102409 Kollision Laserschneidkopf - Ursache: Auflagefl\u00e4che verschmutzt",
                language: "de"
            },
            {
                description: "TG 58102409 Collision laser cutting head - Cause: Support surface dirty",
                language: "en"
            },
            {
                description: "TG 58102409  colisi\u00f3n de Cabezal de corte l\u00e1ser  - Causa: superficie de apoyo sucia",
                language: "es"
            },
            {
                description: "TG 58102409 T\u00eate de d\u00e9coupe laser par collision - Cause : Surface de support sale",
                language: "fr"
            },
            {
                description: "TG 58102409 \u00dctk\u00f6z\u00e9ses l\u00e9zerv\u00e1g\u00f3fej - Ok: Fel\u00fclet szennyezett",
                language: "hu"
            },
            {
                description: "TG 58102409 Testa di taglio laser in collisione - causa: superficie di supporto sporca",
                language: "it"
            },
            {
                description: "TG 58102409 \u30ec\u30fc\u30b6\u30ab\u30c3\u30c6\u30a3\u30f3\u30b0\u30d8\u30c3\u30c9",
                language: "ja"
            },
            {
                description: "TG 58102409 Kolizja g\u0142owicy laserowej - Przyczyna: Zabrudzenie powierzchni przylegania",
                language: "pl"
            },
            {
                description: "+++ This text is machine translated +++\nTG 58102409 Colis\u00e3o de cabe\u00e7a de corte laser",
                language: "pt"
            },
            {
                description: "PR-C 58102409: Lazer kesme kafas\u0131nda \u00e7arp\u0131\u015fma E(%1.%2)",
                language: "tr"
            },
            {
                description: "TG 58102409 \u78b0\u649e\u9433\u5c04\u5207\u5272\u982d - \u539f\u56e0\uff1a\u652f\u6490\u9762\u9ad2\u3002",
                language: "zh-Hant"
            }
        ]
    }
];

const proactiveCaseClosureCss = ".mf-proactive-case-closure{display:block}.mf-proactive-case-closure__loader{padding:50px;position:relative}.mf-proactive-case-closure__result-icon{font-size:50px}@media (min-width: 512px){.mf-proactive-case-closure__result-icon{font-size:100px}}.mf-proactive-case-closure__container{max-width:700px;margin:0 auto}.mf-proactive-case-closure__introduction{text-align:var(--mf-service-calls-intro-align, center)}.mf-proactive-case-closure fieldset{border:0;margin:0;padding:0}.mf-proactive-case-closure__question-container{padding:var(--mf-service-calls-question-container-padding, 30px 60px)}.mf-proactive-case-closure__question{text-align:var(--mf-service-calls-question-align, center);font-family:Frutiger-Light, sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;text-rendering:optimizelegibility;font-weight:400;font-size:var(--mf-service-calls-question-font-size, 23px)}@media only screen and (width <= 1439px){.mf-proactive-case-closure__question{font-size:var(--mf-service-calls-question-font-size-mobile, 20px)}}@media only screen and (width >= 1920px){.mf-proactive-case-closure__question{font-size:28px}}.mf-proactive-case-closure__radio-buttons-container{display:flex;flex-direction:row;margin-top:20px}.mf-proactive-case-closure__radio-buttons-container::before,.mf-proactive-case-closure__radio-buttons-container::after{content:\"\";display:block;width:var(--mf-service-calls-radio-button-container-before-width, 100%)}.mf-proactive-case-closure__radio-buttons{display:flex;flex-direction:row;justify-content:var(--mf-service-calls-radio-button-justify-content, center);flex-wrap:wrap;gap:10px;width:var(--mf-service-calls-radio-buttons-width, auto)}@media (min-width: 512px){ux-modal-content .mf-proactive-case-closure{margin:0 60px}.mf-proactive-case-closure__radio-buttons-container{flex-direction:column}.mf-proactive-case-closure__radio-buttons{text-align:var(--mf-service-calls-radio-button-align, center)}}.mf-proactive-case-closure ux-radio-button{width:var(--mf-service-calls-radio-button-width, 100%)}@media (min-width: 512px){.mf-proactive-case-closure ux-radio-button{width:40%}}.mf-proactive-case-closure__error{color:#FF0201 !important}.mf-proactive-case-closure__button-container{display:flex;flex-direction:column;gap:10px;max-width:900px;margin:0 auto}@media (min-width: 512px){.mf-proactive-case-closure__button-container{margin-top:40px;justify-content:space-between;flex-direction:row}}";
const MfProactiveCaseClosureStyle0 = proactiveCaseClosureCss;

const baseClass = "mf-proactive-case-closure";
const ProactiveCaseClosure = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.cancelEvent = createEvent(this, "mfServiceCallCancel", 7);
        this.successEvent = createEvent(this, "mfServiceCallSuccess", 7);
        this.showSuccessEvent = createEvent(this, "mfServiceCallShowSuccess", 7);
        this.submitButton = null;
        this.apiClient = null;
        this.onSilentAuthError = (e) => {
            if (this.isApp) {
                return;
            }
            this.authError = e.detail.error;
            if (this.authError !== null) {
                this.isAuthenticating = false;
            }
        };
        this.callId = "";
        this.locale = "en-US";
        this.isApp = false;
        this.env = "prod";
        this.technicalGuideProvided = null;
        this.legacyApi = false;
        this.debug = false;
        this.questions = [];
        this.answers = {};
        this.complete = false;
        this.submitting = false;
        this.submitSuccess = false;
        this.submitError = null;
        this.authError = null;
        this.config = null;
        this.loadingTranslations = true;
        this.isAuthenticating = false;
        this.isWaitingForTechnicalGuide = false;
    }
    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.setEnv();
    }
    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeAuth) {
            this.unsubscribeAuth();
        }
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }
    componentWillLoad() {
        if (!this.isApp) {
            this.isAuthenticating = true;
        }
        this.loadTranslations();
        this.updateQuestions();
        this.checkComplete();
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    setEnv() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.apiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.apiClient.subscribeToTbdbToken((authEvent) => {
            if (authEvent.errorMessage) {
                this.authError = authEvent.errorMessage;
            }
            if (authEvent.success) {
                this.checkTechnicalGuideProvided();
            }
            this.isAuthenticating = false;
        });
    }
    checkTechnicalGuideProvided() {
        if (this.isApp)
            return;
        this.isWaitingForTechnicalGuide = true;
        getTechnicalGuides(this.apiClient, this.callId)
            .then((response) => (this.technicalGuideProvided = response && response.length > 0))
            .catch((error) => {
            this.technicalGuideProvided = false;
            // eslint-disable-next-line no-console
            console.error("Error while checking technical guide provided", error);
        })
            .finally(() => (this.isWaitingForTechnicalGuide = false));
    }
    submitSuccessChanged() {
        if (this.submitSuccess) {
            this.showSuccessEvent.emit();
        }
    }
    loadTranslations() {
        this.loadingTranslations = true;
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
            this.loadingTranslations = false;
        });
    }
    submit() {
        const data = buildRequestData(this.answers, this.callId);
        if (this.isApp) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            window.SendRawMessage({ type: "proactiveCaseClosure", data });
            return;
        }
        if (!this.apiClient) {
            this.submitError = "No API client available";
            return;
        }
        this.submitting = true;
        sendCaseClosureRequest(this.apiClient, data)
            .then((response) => {
            if (response) {
                this.submitSuccess = true;
            }
            else {
                this.submitError = "An error occurred while submitting the form";
            }
            this.submitting = false;
        })
            .catch((error) => {
            this.submitError = error || "Unknown error";
            this.submitting = false;
        });
    }
    cancel() {
        this.cancelEvent.emit();
    }
    finish() {
        this.successEvent.emit();
    }
    /**
     * this method collects the questions that can be displayed
     * We start at the initial question and get the next question based on the selected answer
     */
    updateQuestions() {
        const questionsArray = [questions[FIRST_QUESTION_KEY]];
        while (this.hasAnsweredLast(questionsArray) && this.lastAnswerHasNext(questionsArray)) {
            questionsArray.push(this.getNextQuestion(questionsArray[questionsArray.length - 1]));
        }
        this.updateAnswers(questionsArray);
        this.questions = questionsArray;
    }
    checkComplete() {
        this.complete =
            this.hasAnsweredLast(this.questions) &&
                !this.lastAnswerHasNext(this.questions) &&
                this.hasAnsweredComment(this.questions);
    }
    addAnswerTechnicalGuideProvided() {
        if (this.questions.find((question) => question.name === TECHNICAL_GUIDE_LINKED) &&
            this.answers[TECHNICAL_GUIDE_LINKED] === undefined) {
            setTimeout(() => {
                this.answers = Object.assign(Object.assign({}, this.answers), { [TECHNICAL_GUIDE_LINKED]: this.technicalGuideProvided ? FOLLOWUP_QUESTION : TECHNICAL_GUIDE_STATE.NONE });
            }, 0);
            // timeout is a workaround to ensure that the next question is always displayed after the technical guide question
        }
    }
    onQuestionsChange() {
        if (this.complete)
            return;
        setTimeout(() => {
            var _a;
            (_a = this.el.querySelector(`.${baseClass}__question-container:last-child`)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
        }, 200);
    }
    onCompletionChange() {
        if (this.complete) {
            setTimeout(() => {
                var _a;
                (_a = this.submitButton) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
            }, 200);
        }
    }
    hasAnsweredLast(questionsArray) {
        return this.answers[questionsArray[questionsArray.length - 1].name] !== undefined;
    }
    lastAnswerHasNext(questionsArray) {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        return lastQuestion.options[this.answers[lastQuestion.name]].next !== undefined;
    }
    getNextQuestion(currentQuestion) {
        const currentAnswer = this.answers[currentQuestion.name];
        return questions[currentQuestion.options[currentAnswer].next];
    }
    hasAnsweredComment(questionsArray) {
        const lastQuestion = questionsArray[questionsArray.length - 1];
        if (!lastQuestion.comment) {
            return true;
        }
        const commentAnswer = this.answers[lastQuestion.comment.name];
        return commentAnswer && commentAnswer.trim() !== "";
    }
    /** this method removes those answers where the associated question has been removed */
    updateAnswers(questionsArray) {
        const answersObject = Object.assign({}, this.answers);
        Object.keys(answersObject).forEach((key) => {
            if (!questionsArray.find((question) => question.name === key) &&
                !questionsArray.find((question) => { var _a; return ((_a = question.comment) === null || _a === void 0 ? void 0 : _a.name) === key; })) {
                delete answersObject[key];
            }
        });
        if (JSON.stringify(answersObject) !== JSON.stringify(this.answers)) {
            this.answers = answersObject;
        }
    }
    render() {
        var _a, _b, _c, _d;
        const t = (key, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const isLoading = this.loadingTranslations || this.isAuthenticating || this.isWaitingForTechnicalGuide;
        if (!this.isApp && this.callId === "") {
            return h(Host, { "data-error": "no callId provided" });
        }
        if (this.config === null) {
            return h(Host, { "data-error": "no config" });
        }
        const silentAuth = !this.isApp && (h("ux-u-silent-auth", { clientName: authClientName, clientId: (_a = this.config) === null || _a === void 0 ? void 0 : _a.authClientId, issuer: (_b = this.config) === null || _b === void 0 ? void 0 : _b.authIssuer, debug: (_c = this.config) === null || _c === void 0 ? void 0 : _c.logging, scope: (_d = this.config) === null || _d === void 0 ? void 0 : _d.authScope, onUxAuth: (e) => this.onSilentAuthError(e) }));
        if (isLoading) {
            return (h(Host, { class: baseClass }, silentAuth, h("div", { class: `${baseClass}__loader` }, h("ux-loader", { variant: "spinner" }))));
        }
        if (this.submitSuccess) {
            return (h(Host, { class: baseClass }, silentAuth, h("div", { class: `${baseClass}__container` }, h("div", { class: "ux-u-text-align--center ux-u-mb-xxs" }, h("ux-icon", { class: `${baseClass}__result-icon ux-color-success`, name: "checkmark", weight: "light" })), h("h1", { class: "ux-text-headline ux-text-headline--big ux-u-text-align--center" }, t("serviceCase.proactiveCaseClosure.dialogTitle")), h("div", { class: "ux-u-text-align--center" }, h("ux-button", { onClick: () => this.finish(), variant: "primary" }, t("serviceCase.proactiveCaseClosure.dialog.buttonOk"))))));
        }
        if (!this.isApp && this.authError) {
            return (h(Host, { class: baseClass }, silentAuth, h("div", { class: `${baseClass}__container` }, h("div", { class: "ux-u-text-align--center ux-u-mb-xxs" }, h("ux-icon", { class: `${baseClass}__result-icon ux-color-error`, name: "cross", weight: "light" })), h("h1", { class: "ux-text-headline ux-text-headline--big ux-u-text-align--center" }, "Error: Not authenticated"), h("p", { class: "ux-text-normal ux-u-text-align--center ux-u-margin-vertical-20" }, this.authError), h("div", { class: "ux-u-text-align--center" }, h("ux-button", { onClick: () => this.cancel() }, t("button.cancel"))))));
        }
        return (h(Host, { class: baseClass }, silentAuth, h("div", { class: `${baseClass}__container` }, !this.isApp && (h("h1", { class: "ux-text-headline ux-text-headline--big ux-u-text-align--center" }, t("serviceCase.proactiveCaseClosure.title"))), h("p", { class: `${baseClass}__introduction ux-text-normal` }, t("serviceCase.proactiveCaseClosure.instructions")), this.questions.map((question) => question.invisible ? null : (h("div", { class: `${baseClass}__question-container ux-u-shadow ux-u-background-white ux-u-margin-vertical-20` }, h(QuestionRadio, { t: t, question: question, answers: this.answers, setAnswers: (answers) => {
                this.answers = answers;
            }, baseClass: baseClass }), question.comment && (h(QuestionComment, { label: t(question.comment.labelKey), placeholder: t(question.comment.placeholderKey), name: question.comment.name, answers: this.answers, setAnswers: (answers) => {
                this.answers = answers;
            } }))))), this.submitError && h("p", { class: `${baseClass}__error ux-text-normal` }, this.submitError)), h("div", { class: `${baseClass}__button-container` }, !this.isApp && (h("ux-button", { onClick: () => this.cancel(), class: `${baseClass}__cancel-button` }, t("button.cancel"))), h("ux-button", { onClick: () => this.submit(), ref: (el) => (this.submitButton = el), class: `${baseClass}__submit-button`, variant: "primary", disabled: !this.complete || this.submitting }, t("serviceCase.proactiveCaseClosure.question.submit")))));
    }
    get el() { return getElement(this); }
    static get watchers() { return {
        "locale": ["onLocaleChange"],
        "env": ["setEnv"],
        "submitSuccess": ["submitSuccessChanged"],
        "answers": ["updateQuestions", "checkComplete", "addAnswerTechnicalGuideProvided"],
        "questions": ["checkComplete", "addAnswerTechnicalGuideProvided", "onQuestionsChange"],
        "complete": ["onCompletionChange"]
    }; }
};
ProactiveCaseClosure.style = MfProactiveCaseClosureStyle0;

export { ProactiveCaseClosure as mf_proactive_case_closure };

//# sourceMappingURL=mf-proactive-case-closure.entry.js.map